import React from 'react';
import {
  adminApprove,
  adminDisapprove,
  getPharmacyDetails,
  PermissionsOfAdmin,
  PermissionsRemovalKeys,
  ServiceTypes,
  TypeOfUser,
} from '@pharmaplan/common';
import { FormikValues } from 'formik';
import { useNavigate } from 'react-router-dom';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import { useAppDispatch } from '../../useAppDispatch';
import strings from '../../../localization';

import { renderScreen } from '../../../actions/drawerActions';
import {
  Constants,
  DeactivateModalIcon,
  OtherScreens,
  ScreenTypes,
  SecondaryAdminNavigation,
} from '../../../helpers/Constants';
import useDrawer from '../../useDrawer';
import { setDialog } from '../../../reducers/dialogReducer';
import DeactivateDialog from '../../../components/Admin/PharmacistList/DeactivateDialog';
import useAdminPermissions from '../useAdminPermissions';
import useAdminBookingBlock from '../useAdminBookingBlock';

const isFullService = ServiceHandler.getService() === ServiceTypes.full;

export interface IPharmacyListActionsParams {
  pharmacyId: string;
  name: string;
  email?: string;
  appointedPharmacyId?: string | null;
  planName?: string | null;
}

const { Edit, Deactivate } = PermissionsRemovalKeys;
export const keysToRemove = {
  ViewPharmacyAdmins: '4',
  Deactivate,
  ViewRating: 'ViewRating',
};

const usePharmacyListActions = (isAppointed: boolean) => {
  const dispatch = useAppDispatch();
  const { openDrawer } = useDrawer();
  const navigate = useNavigate();
  const { can } = useAdminPermissions();

  const cannotActivate = !can(PermissionsOfAdmin.Activation);
  const cannotPost = !can(PermissionsOfAdmin.Posting);
  const cannotChangeParameters = !can(PermissionsOfAdmin.ChangeParameters);

  const { actionRequiredDialog, disableActionShifts } = useAdminBookingBlock();

  const showCreateWorkshift = (pharmacyId: string, name: string, planName?: string | null) =>
    () => {
      if (disableActionShifts(planName)) {
        actionRequiredDialog();
        return;
      }

      openDrawer();
      dispatch(
        renderScreen({
          screenNumber: 1,
          screenType: ScreenTypes.pharmacyList,
          pharmacyId,
          extraParameters: {
            name,
          },
        }),
      );
    };

  const showEditPharmacy = (pharmacyId: string, appointedPharmacyId?: string | null) =>
    () => {
      dispatch(getPharmacyDetails(pharmacyId));
      openDrawer();
      dispatch(
        renderScreen({
          screenNumber: 3,
          screenType: ScreenTypes.profile,
          fromScreen:
            isAppointed || !!appointedPharmacyId
              ? OtherScreens.AppointedPharmacies
              : SecondaryAdminNavigation.pharmacyList,
        }),
      );
    };

  const showPharmacyProfile = (pharmacyId: string) => {
    openDrawer();
    dispatch(
      renderScreen({
        screenType: ScreenTypes.profile,
        screenNumber: 1,
        pharmacyId,
      }),
    );
  };

  const showListOfPharmacists = (pharmacyId: string) =>
    () => {
      openDrawer();
      dispatch(
        renderScreen({
          screenNumber: 2,
          screenType: ScreenTypes.pharmacyList,
          pharmacyId,
        }),
      );
    };

  const showListOfWorkshifts = (pharmacyId: string) =>
    () => {
      navigate(Constants.paths.admin.listOfWorkshifts, {
        state: pharmacyId,
      });
    };

  const showIncompatibleMatch = (pharmacyId: string, pharmacyEmail: string) =>
    () => {
      navigate(Constants.paths.admin.pharmacyListIncompatible, {
        state: { pharmacyId, pharmacyEmail },
      });
    };

  const deactivatePharmacy = (values: FormikValues) => {
    const { id, reason } = values ?? {};

    dispatch(adminDisapprove({ id, reason }));
  };

  const activatePharmacy = (values: FormikValues) => {
    const { id, reason } = values ?? {};

    dispatch(adminApprove({ id, reason }));
  };

  const deactivateModal = (pharmacyId: string) =>
    () => {
      dispatch(
        setDialog({
          Component: (
            <DeactivateDialog
              id={pharmacyId}
              iconType={DeactivateModalIcon.warning}
              confirmationMessage={strings.areYouSureDeactivatePharmacy}
              initialValues={{
                id: pharmacyId,
                reason: '',
              }}
              heading={
              strings.formatString(
                strings.deactivateFormat,
                strings.pharmacy,
              ) as string
            }
              onSubmit={deactivatePharmacy}
            />
          ),
          heading: {
            title: '',
          },
          showCloseButton: true,
        }),
      );
    };

  const viewRatings = (pharmacyId: string) =>
    () => {
      navigate(Constants.paths.admin.pharmacyViewRatings, {
        state: { id: pharmacyId, user: TypeOfUser.pharmacy },
      });
    };

  const activateModal = (pharmacyId: string) =>
    () => {
      dispatch(
        setDialog({
          Component: (
            <DeactivateDialog
              id={pharmacyId}
              iconType={DeactivateModalIcon.positiveConfirmation}
              confirmationMessage={strings.areYouSureActivatePharmacy}
              initialValues={{
                id: pharmacyId,
                reason: '',
              }}
              heading={
              strings.formatString(
                strings.activateFormat,
                strings.pharmacy,
              ) as string
            }
              onSubmit={activatePharmacy}
            />
          ),
          heading: {
            title: '',
          },
          showCloseButton: true,
        }),
      );
    };

  const actions = (params: IPharmacyListActionsParams) => {
    let arr = [
      {
        key: '1',
        label: strings.createWorkshift,
        onClick: showCreateWorkshift(
          params.pharmacyId,
          params?.name ?? '',
          params?.planName,
        ),
      },
      {
        key: '2',
        label: strings.formatString(strings.view, strings.listOfWorkshifts),
        onClick: showListOfWorkshifts(params.pharmacyId),
      },
      {
        key: '3',
        label: strings.formatString(strings.view, strings.listOfProfessionals),
        onClick: showListOfPharmacists(params.pharmacyId),
      },
      {
        key: '5',
        label: strings.formatString(strings.view, strings.incompatibleMatch),
        onClick: showIncompatibleMatch(params.pharmacyId, params.name),
      },
      {
        key: keysToRemove.ViewRating,
        label: strings.viewRating,
        onClick: viewRatings(params.pharmacyId),
      },
      {
        key: Deactivate,
        label: strings.deactivate,
        onClick: deactivateModal(params.pharmacyId),
      },
      {
        key: Edit,
        label: strings.editProfileOnly,
        onClick: showEditPharmacy(
          params.pharmacyId,
          params.appointedPharmacyId,
        ),
      },
    ];

    if (cannotChangeParameters) {
      arr = arr.filter((item) =>
        item.key !== Edit);
    }
    if (cannotPost) {
      arr = arr.filter((item) =>
        item.key !== '1');
    }
    if (cannotActivate) {
      arr = arr.filter((item) =>
        item.key !== Deactivate);
    }

    if (isFullService) {
      arr = arr.filter((item) =>
        item.key !== keysToRemove.ViewRating);
    }

    if (params.appointedPharmacyId) {
      arr = arr.filter((item) =>
        ![Deactivate as string].includes(item.key));
    }

    return arr;
  };

  return {
    actions,
    showPharmacyProfile,
    activateModal,
  };
};

export default usePharmacyListActions;
